import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/api";
import {
  FaUser,
  FaBox,
  FaHeart,
  FaMapMarkerAlt,
  FaCreditCard,
  FaTrashAlt,
  FaEdit,
  FaPlus,
  FaCalendar,
  FaTag,
  FaDollarSign,
  FaTruck,
  FaEye,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import EditProfile from "./EditProfile";
import ChangePassword from "./ChangePassword";
import AddressForm from "./AddressForm";
import PaymentMethodForm from "./PaymentMethodForm";
import { useLocation, useNavigate } from "react-router-dom";
import { formatCurrency, formatDateTime } from "../../utils/helpers";
import { getImageUrl } from "../../utils/imageUrl";
import { useTranslation } from "react-i18next";

const UserProfile = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [addressToEdit, setAddressToEdit] = useState(null);

  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [methodToEdit, setMethodToEdit] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const getTabFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get("tab") || "profile";
  };

  const [activeTab, setActiveTab] = useState(getTabFromUrl());

  useEffect(() => {
    setActiveTab(getTabFromUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const addressesResponse = await api.get("/user/addresses");
      setAddresses(addressesResponse.data);
    } catch (error) {
      console.error("Failed to fetch addresses:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const response = await api.get(`/user/payment-methods`);
      setPaymentMethods(response.data);
    } catch (error) {
      console.error("Failed to fetch payment methods:", error);
      if (error.response && error.response.status === 404) {
        setPaymentMethods([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get status badge style
  const getStatusBadge = (status) => {
    switch (status) {
      case "delivered":
        return "bg-green-100 text-green-800";
      case "shipped":
        return "bg-blue-100 text-blue-800";
      case "processing":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        if (activeTab === "wishlist") {
          const wishlistResponse = await api.get("/user/wishlist");
          setWishlist(wishlistResponse.data);
        }
        if (activeTab === "orders") {
          const ordersResponse = await api.get("/orders");
          setOrders(ordersResponse.data);
        }
        if (activeTab === "addresses") {
          fetchAddresses();
        }
        if (activeTab === "payments") {
          fetchPaymentMethods();
        }
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeTab]);

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const removeFromWishlist = async (e, wishlist_id) => {
    e.stopPropagation();
    try {
      await api.delete(`/user/wishlist/${wishlist_id}`);
      setWishlist((prevWishlist) =>
        prevWishlist.filter((item) => item.wishlist_id !== wishlist_id),
      );
      console.log("Product removed from wishlist successfully.");
    } catch (error) {
      console.error("Failed to remove product from wishlist:", error);
    }
  };

  // --- Address Handlers (Existing) ---
  const handleDeleteAddress = async (e, address_id) => {
    e.stopPropagation();
    try {
      await api.delete(`/user/addresses/${address_id}`);
      setAddresses((prevAddresses) =>
        prevAddresses.filter((address) => address.address_id !== address_id),
      );
      console.log("Address removed successfully.");
    } catch (error) {
      console.error("Failed to remove address:", error);
    }
  };

  const handleAddAddressClick = () => {
    setIsAdding(true);
    setAddressToEdit(null);
  };

  const handleEditAddressClick = (address) => {
    setIsAdding(false);
    setAddressToEdit(address);
  };

  const handleSaveAddress = () => {
    setIsAdding(false);
    setAddressToEdit(null);
    fetchAddresses();
  };

  const handleCancelForm = () => {
    setIsAdding(false);
    setAddressToEdit(null);
  };
  // ------------------------------------

  // --- Payment Method Handlers (New/Updated) ---
  const handleDeletePaymentMethod = async (e, payment_method_id) => {
    e.stopPropagation();
    if (window.confirm(t("profile.payment.confirmDelete"))) {
      try {
        // The delete endpoint uses soft-delete (is_active: false)
        await api.delete(`/user/payment-methods/${payment_method_id}`);
        // Refresh the list to remove the inactive card
        fetchPaymentMethods();
        console.log(
          "Payment method removed successfully (marked as inactive).",
        );
      } catch (error) {
        console.error("Failed to remove payment method:", error);
      }
    }
  };

  const handleAddPaymentClick = () => {
    setIsAddingPayment(true);
    setMethodToEdit(null);
  };

  const handleEditPaymentClick = (method) => {
    // Only set the method to edit. The form will handle what fields are editable.
    setIsAddingPayment(false);
    setMethodToEdit(method);
  };

  const handleSavePayment = () => {
    setIsAddingPayment(false);
    setMethodToEdit(null);
    fetchPaymentMethods(); // Refresh the list
  };

  const handleCancelPaymentForm = () => {
    setIsAddingPayment(false);
    setMethodToEdit(null);
  };
  // ------------------------------------

  const handleProfileUpdate = () => {
    setActiveTab("profile");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl text-gray-600">{t("profile.loading")}</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center mt-10 p-6 rounded-lg shadow-md bg-red-100 text-red-700">
        {t("profile.authRequiredView")}
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "profile":
        return (
          <div className="p-6">
            <h2 className="text-2xl font-semibold text-gray-700 mb-4">
              {t("profile.personalInfo")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="font-medium text-gray-600">
                  {t("profile.fields.firstNameLabel")}
                </p>
                <p className="text-lg text-gray-800">{user.first_name}</p>
              </div>
              <div>
                <p className="font-medium text-gray-600">
                  {t("profile.fields.lastNameLabel")}
                </p>
                <p className="text-lg text-gray-800">{user.last_name}</p>
              </div>
              <div>
                <p className="font-medium text-gray-600">
                  {t("profile.fields.emailLabel")}
                </p>
                <p className="text-lg text-gray-800">{user.email}</p>
              </div>
              <div>
                <p className="font-medium text-gray-600">
                  {t("profile.fields.phoneNumberLabel")}
                </p>
                <p className="text-lg text-gray-800">
                  {user.phone_number || t("profile.na")}
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => setActiveTab("editProfile")}
                className="w-full sm:w-auto text-center px-6 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {t("profile.actions.editProfile")}
              </button>
              <button
                onClick={() => setActiveTab("changePassword")}
                className="w-full sm:w-auto text-center px-6 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                {t("profile.actions.changePassword")}
              </button>
            </div>
          </div>
        );

      case "orders":
        return (
          <div className="p-6">
            <h2 className="text-3xl font-bold text-[#83375B] mb-8 border-b pb-2">
              {t("profile.tabs.orders")}
            </h2>
            {orders.length > 0 ? (
              <div className="space-y-6">
                {orders.map((order) => (
                  <div
                    key={order.order_id}
                    className="bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-shadow p-6"
                  >
                    {/* --- Header Row: ID, Date, Status --- */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b pb-4 mb-4">
                      <div className="flex items-center space-x-4 mb-3 sm:mb-0">
                        <FaTag className="text-[#83375B] w-6 h-6" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">
                            {t("profile.order.orderNo")}
                          </p>
                          {/* Use order_number for customer reference, fall back to order_id if not available */}
                          <p className="text-xl font-bold text-gray-900">
                            {order.order_number || `#${order.order_id}`}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-500">
                            {t("profile.order.datePlaced")}
                          </p>
                          <p className="text-base text-gray-700 font-medium flex items-center justify-end">
                            <FaCalendar className="mr-1 text-gray-400" />
                            {formatDateTime(order.paid_at)}
                          </p>
                        </div>

                        <span
                          className={`px-3 py-1 text-sm font-semibold rounded-full capitalize ${getStatusBadge(
                            order.status,
                          )}`}
                        >
                          {order.status}
                        </span>
                      </div>
                    </div>

                    {/* --- Details Row: Total, Shipping, Tracking --- */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-700 mb-6">
                      {/* Total Price */}
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-500 flex items-center">
                          <FaDollarSign className="mr-2 text-green-500" />{" "}
                          {t("profile.order.total")}
                        </span>
                        <span className="text-lg font-extrabold text-[#83375B]">
                          {formatCurrency(order.total_price)}
                        </span>
                      </div>

                      {/* Shipping Method */}
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-500 flex items-center">
                          <FaTruck className="mr-2 text-gray-500" />{" "}
                          {t("profile.order.shipping")}
                        </span>
                        <span className="font-medium capitalize">
                          {order.shipping_method || "N/A"}
                        </span>
                      </div>

                      {/* Tracking Number */}
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-500 flex items-center">
                          <FaMapMarkerAlt className="mr-2 text-blue-500" />{" "}
                          {t("profile.order.tracking")}
                        </span>
                        {order.tracking_number ? (
                          <a
                            href={`#tracking-link-placeholder/${order.tracking_number}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 font-medium truncate"
                            title={order.tracking_number}
                          >
                            {order.tracking_number}
                          </a>
                        ) : (
                          <span className="text-gray-500">
                            {t("profile.na")}
                          </span>
                        )}
                      </div>

                      {/* Payment Status (Optional, but useful) */}
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-500 flex items-center">
                          <FaDollarSign className="mr-2 text-yellow-500" />{" "}
                          {t("profile.order.paymentStatus")}
                        </span>
                        <span className="font-medium capitalize text-green-600">
                          {order.payment_status}
                        </span>
                      </div>
                    </div>

                    {/* --- Action Button --- */}
                    <div className="flex justify-end pt-4 border-t">
                      {/* Assuming you will have an OrderDetails page */}
                      <Link
                        to={`/user/orders/${order.order_id}`}
                        className="flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-[#AA7F96] hover:bg-[#916A80] transition-colors shadow-md"
                      >
                        <FaEye className="mr-2" />{" "}
                        {t("profile.order.viewDetails")}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10 border border-gray-200 rounded-xl bg-gray-50">
                <p className="text-xl text-gray-500">
                  {t("profile.order.empty")}
                </p>
                {/* Optional: Add a button to navigate to the shop */}
              </div>
            )}
          </div>
        );
      case "wishlist":
        return (
          <div className="p-6">
            <h2 className="text-2xl font-semibold text-gray-700 mb-4">
              {t("profile.tabs.wishlist")}
            </h2>
            {wishlist.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-5 gap-6">
                {wishlist.map((item) => {
                  const product = item.Product;
                  const productName = product.name_en;
                  const imageUrl =
                    product.images && product.images.length > 0
                      ? getImageUrl(product.images[0].image_url)
                      : `https://placehold.co/400x300/E0BBE4/FFFFFF?text=No+Image`;

                  return (
                    <div
                      key={item.wishlist_id}
                      className="bg-gray-50 p-4 rounded-lg shadow-sm flex flex-col cursor-pointer hover:shadow-lg transition-all relative"
                      onClick={() => handleProductClick(product.product_id)}
                    >
                      <img
                        src={imageUrl}
                        alt={productName}
                        className="w-full h-40 object-cover rounded-t-lg"
                      />
                      <button
                        onClick={(e) => removeFromWishlist(e, item.wishlist_id)}
                        className="absolute top-2 right-2 p-2 rounded-full text-[rgb(153_46_26)] bg-white hover:bg-[rgb(215_215_215)] transition-colors"
                        title={t("profile.wishlist.remove")}
                      >
                        <FaHeart className="w-5 h-5" />
                      </button>
                      <div className="flex-grow text-center mt-2">
                        <p className="font-semibold text-lg">{productName}</p>
                        <p className="text-sm text-gray-600">
                          {t("profile.wishlist.priceLabel")} {product.price} TL
                        </p>
                        <p className="text-xs text-gray-500">
                          {product.stock_quantity > 0
                            ? t("profile.wishlist.inStock")
                            : t("profile.wishlist.outOfStock")}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500">{t("profile.wishlist.empty")}</p>
            )}
          </div>
        );
      case "addresses":
        if (isAdding || addressToEdit) {
          return (
            <div className="p-6">
              <AddressForm
                onSave={handleSaveAddress}
                onCancel={handleCancelForm}
                addressToEdit={addressToEdit}
              />
            </div>
          );
        }

        return (
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-gray-700">
                {t("profile.tabs.addresses")}
              </h2>
              <button
                onClick={handleAddAddressClick}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                <FaPlus /> <span>{t("profile.address.addNew")}</span>
              </button>
            </div>
            {addresses.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {addresses.map((address) => (
                  <div
                    key={address.address_id}
                    className="bg-white p-6 rounded-lg shadow-md border border-gray-200 relative"
                  >
                    <h3 className="text-lg font-semibold text-gray-800 mb-2 capitalize">
                      {address.address_type}{" "}
                      {t("profile.address.addressSuffix")}
                    </h3>
                    <p>{address.address_line_1}</p>
                    {address.address_line_2 && <p>{address.address_line_2}</p>}
                    <p>
                      {address.city}, {address.state_province}
                    </p>
                    <p>
                      {address.postal_code}, {address.country}
                    </p>
                    <div className="mt-4 flex space-x-2">
                      <button
                        onClick={(e) => handleEditAddressClick(address)}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                        title={t("profile.address.edit")}
                      >
                        <FaEdit className="inline-block mr-1" />{" "}
                        {t("profile.address.edit")}
                      </button>
                      <button
                        onClick={(e) =>
                          handleDeleteAddress(e, address.address_id)
                        }
                        className="text-red-500 hover:text-red-700 transition-colors"
                        title={t("profile.address.delete")}
                      >
                        <FaTrashAlt className="inline-block mr-1" />{" "}
                        {t("profile.address.delete")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">{t("profile.address.empty")}</p>
            )}
          </div>
        );
      case "payments":
        // New Payment Form/View logic
        if (isAddingPayment || methodToEdit) {
          return (
            <div className="p-6">
              <PaymentMethodForm
                onSave={handleSavePayment}
                onCancel={handleCancelPaymentForm}
                methodToEdit={methodToEdit}
              />
            </div>
          );
        }

        return (
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-gray-700">
                {t("profile.tabs.payments")}
              </h2>
              <button
                onClick={handleAddPaymentClick}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                <FaPlus /> <span>{t("profile.payment.addNew")}</span>
              </button>
            </div>
            {paymentMethods.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paymentMethods.map((method) => (
                  <div
                    key={method.payment_method_id}
                    className="bg-white p-6 rounded-lg shadow-md border border-gray-200 relative flex flex-col justify-between"
                  >
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                        <FaCreditCard className="mr-2 text-blue-500" />
                        {method.card_brand || method.method_type}
                        {method.is_default && (
                          <span className="ml-2 px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                            {t("profile.payment.default")}
                          </span>
                        )}
                      </h3>
                      {/* Access fields directly (last4, exp_month) based on the model */}
                      {method.last4 && (
                        <p className="text-xl font-mono tracking-wider">
                          **** **** **** {method.last4}
                        </p>
                      )}
                      {method.exp_month && method.exp_year && (
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">
                            {t("profile.payment.expiresLabel")}
                          </span>
                          {` ${String(method.exp_month).padStart(2, "0")}/${
                            method.exp_year
                          }`}
                        </p>
                      )}
                      {method.cardholder_name && (
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">
                            {t("profile.payment.nameLabel")}
                          </span>{" "}
                          {method.cardholder_name}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        Tokenized via {method.psp_name}
                      </p>
                    </div>
                    <div className="mt-4 flex space-x-2">
                      <button
                        onClick={() => handleEditPaymentClick(method)}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                        title={t("profile.payment.edit")}
                      >
                        <FaEdit className="inline-block mr-1" />{" "}
                        {t("profile.payment.edit")}
                      </button>
                      <button
                        onClick={(e) =>
                          handleDeletePaymentMethod(e, method.payment_method_id)
                        }
                        className="text-red-500 hover:text-red-700 transition-colors"
                        title={t("profile.payment.delete")}
                      >
                        <FaTrashAlt className="inline-block mr-1" />{" "}
                        {t("profile.payment.delete")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">{t("profile.payment.empty")}</p>
            )}
          </div>
        );
      case "editProfile":
        return <EditProfile onUpdateSuccess={handleProfileUpdate} />;
      case "changePassword":
        return <ChangePassword />;
      default:
        return null;
    }
  };

  const tabs = [
    { id: "profile", label: t("profile.tabs.profile"), icon: <FaUser /> },
    { id: "orders", label: t("profile.tabs.orders"), icon: <FaBox /> },
    { id: "wishlist", label: t("profile.tabs.wishlist"), icon: <FaHeart /> },
    {
      id: "addresses",
      label: t("profile.tabs.addresses"),
      icon: <FaMapMarkerAlt />,
    },
    {
      id: "payments",
      label: t("profile.tabs.payments"),
      icon: <FaCreditCard />,
    },
  ];

  const mainTabs = tabs.filter(
    (tab) => tab.id !== "editProfile" && tab.id !== "changePassword",
  );

  return (
    <div className="canvas-background w-full max-w-full min-h-[40rem] container mx-auto p-4 sm:p-6 lg:px-[8rem] lg:py-[5rem] min-h-screen pb-12">
      <div className="mt-[5rem] lg:mt-[4rem] bg-white rounded-lg shadow-md border border-gray-200">
        <div className="border-b border-gray-200">
          <nav
            className="-mb-px flex space-x-8 px-6 overflow-x-auto"
            aria-label="Tabs"
          >
            {mainTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 py-4 px-1 inline-flex items-center space-x-2 text-sm font-medium border-b-2 whitespace-nowrap
                  ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
              >
                {tab.icon}
                <span style={{ marginRight: "0.5rem" }}>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default UserProfile;
